import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  Calendar,
  Settings,
  LogOut,
  Briefcase,
  Award,
  Database,
  FolderOpen,
  Image,
  Handshake,
  Car,
  UserCheck,
  Truck,
  Package,
  BarChart3,
  CreditCard,
  Globe,
  Bell,
  Shield,
  Phone,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import type { AdminUser } from '../types';
import { pageSettingsService } from '../services/pageSettingsService';

interface SidebarProps {
  user: AdminUser | null;
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar = ({ user, isOpen, onClose }: SidebarProps) => {
  const [logoUrl, setLogoUrl] = useState<string>('/logo.svg');
  const navigate = useNavigate();
  
  // Fetch logo from about page settings
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const ps = await pageSettingsService.get('about');
        if (ps?.data?.logoUrl) {
          setLogoUrl(ps.data.logoUrl);
        }
      } catch (error) {
        console.error('Failed to fetch logo:', error);
      }
    };
    
    fetchLogo();
  }, []);

  const [contentManagementOpen, setContentManagementOpen] = useState(false);

  const menuItems = [
    // DaChauffeur Menu Items
    {
      title: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin/dashboard',
      roles: ['super-admin', 'admin', 'editor'],
    },
    {
      title: 'Bookings',
      icon: Calendar,
      path: '/admin/bookings',
      roles: ['super-admin', 'admin', 'editor'],
    },
    {
      title: 'Drivers',
      icon: UserCheck,
      path: '/admin/drivers',
      roles: ['super-admin', 'admin'],
    },
    {
      title: 'Fleet',
      icon: Truck,
      path: '/admin/fleet',
      roles: ['super-admin', 'admin', 'editor'],
    },
    {
      title: 'Customers',
      icon: Users,
      path: '/admin/customers',
      roles: ['super-admin', 'admin', 'editor'],
    },
    {
      title: 'Extras',
      icon: Package,
      path: '/admin/extras',
      roles: ['super-admin', 'admin', 'editor'],
    },
    {
      title: 'Reports',
      icon: BarChart3,
      path: '/admin/reports',
      roles: ['super-admin', 'admin', 'editor'],
    },
    {
      title: 'Settings',
      icon: Settings,
      path: '/admin/settings',
      roles: ['super-admin', 'admin'],
    },
    {
      title: 'API Settings',
      icon: Shield,
      path: '/admin/api-settings',
      roles: ['super-admin'],
    },
    {
      title: 'Content Management',
      icon: FileText,
      path: '/admin/content',
      roles: ['super-admin', 'admin', 'editor'],
      isSubmenu: true,
      isOpen: contentManagementOpen,
      onToggle: () => setContentManagementOpen(!contentManagementOpen),
      submenu: [
        {
          title: 'Content Hub',
          icon: LayoutDashboard,
          path: '/admin/content',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Universal Content',
          icon: FileText,
          path: '/admin/content/universal',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Homepage',
          icon: LayoutDashboard,
          path: '/admin/content/homepage',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Services',
          icon: Briefcase,
          path: '/admin/content/services',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Service Pages',
          icon: Car,
          path: '/admin/content/service-pages',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Fleet Management',
          icon: Truck,
          path: '/admin/content/fleet',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Pricing',
          icon: CreditCard,
          path: '/admin/content/pricing',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Booking System',
          icon: Calendar,
          path: '/admin/content/booking',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'About Us',
          icon: Award,
          path: '/admin/content/about',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Contact',
          icon: Phone,
          path: '/admin/content/contact',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Gallery',
          icon: Image,
          path: '/admin/content/gallery',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Careers',
          icon: Briefcase,
          path: '/admin/content/careers',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'FAQ',
          icon: FileText,
          path: '/admin/content/faq',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Resources',
          icon: FolderOpen,
          path: '/admin/content/resources',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'News & Events',
          icon: Bell,
          path: '/admin/content/news-events',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Projects',
          icon: Database,
          path: '/admin/content/projects',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Sectors',
          icon: Globe,
          path: '/admin/content/sectors',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Partners',
          icon: Handshake,
          path: '/admin/content/partners',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'RFQ/RFP',
          icon: FileText,
          path: '/admin/content/rfq-rfp',
          roles: ['super-admin', 'admin', 'editor'],
        },
        {
          title: 'Terms & Policies',
          icon: Shield,
          path: '/admin/content/policies',
          roles: ['super-admin', 'admin', 'editor'],
        },
      ]
    },
    {
      title: 'Users',
      icon: Users,
      path: '/admin/users',
      roles: ['super-admin', 'admin'],
    },
  ];

  const hasAccess = (requiredRoles: string[]) => {
    if (!user) return false;
    return requiredRoles.includes(user.role);
  };

  const filteredMenuItems = menuItems.filter(item => hasAccess(item.roles));

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-gradient-to-b from-gray-900 to-gray-800 text-white transform transition-transform duration-300 ease-in-out z-40 shadow-2xl flex flex-col overflow-hidden
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:shadow-xl
      `}>
        {/* User Info */}
        <div className="p-4 border-b border-gray-700 flex-shrink-0 bg-gray-800">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm">
              <span className="text-sm font-semibold text-white">
                {(() => {
                  if (!user) return 'A';
                  const firstName = typeof user.firstName === 'string' ? user.firstName : user.firstName?.en;
                  return (user.name || firstName || 'A').charAt(0).toUpperCase();
                })()}
              </span>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-white">
                {user ? (user?.name || `${user?.firstName?.en || user?.firstName || ''} ${user?.lastName?.en || user?.lastName || ''}`.trim()) : 'Admin User'}
              </div>
              <div className="text-xs text-gray-400 capitalize">
                {user ? (user?.role?.replace('-', ' ') || 'admin') : 'admin'}
              </div>
            </div>
          </div>
        </div>

        {/* Logo */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-center">
            <img 
              src={logoUrl} 
              alt="Drop of Change Logo" 
              className="h-12 w-auto max-w-[200px]"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {/* DaChauffeur Menu Items */}
            {filteredMenuItems.map((item) => (
              <li key={item.path}>
                {item.isSubmenu ? (
                  // Submenu item
                  <div>
                    <button
                      onClick={() => {
                        item.onToggle();
                        onClose();
                      }}
                      className={`
                        flex items-center justify-between w-full px-3 py-2 rounded-lg transition-all duration-200 font-medium text-sm
                        ${item.path === location.pathname 
                          ? 'bg-blue-600 text-white shadow-lg' 
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-3">
                        <item.icon size={18} />
                        <span>{item.title}</span>
                      </div>
                      {item.isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    
                    {/* Submenu items */}
                    {item.isOpen && item.submenu && (
                      <ul className="mt-1 ml-4 space-y-1">
                        {item.submenu
                          .filter(subItem => hasAccess(subItem.roles))
                          .map((subItem) => (
                            <li key={subItem.path}>
                              <NavLink
                                to={subItem.path}
                                onClick={() => onClose()}
                                className={({ isActive }) => `
                                  flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 font-medium text-sm
                                  ${isActive 
                                    ? 'bg-blue-600 text-white shadow-lg' 
                                    : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                                  }
                                `}
                              >
                                <subItem.icon size={16} />
                                <span>{subItem.title}</span>
                              </NavLink>
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  // Regular menu item
                  <NavLink
                    to={item.path}
                    onClick={() => onClose()}
                    className={({ isActive }) => `
                      flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 font-medium text-sm
                      ${isActive 
                        ? 'bg-blue-600 text-white shadow-lg' 
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }
                    `}
                  >
                    <item.icon size={18} />
                    <span>{item.title}</span>
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex-shrink-0">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 w-full px-3 py-2 text-red-400 hover:bg-red-600 hover:text-white rounded-lg transition-all duration-200 font-medium text-sm"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
